import type { ComponentProps } from 'react'

type Variant = 'primary' | 'compact' | 'secondary'

interface ButtonProps extends Omit<ComponentProps<'button'>, 'className'> {
  variant?: Variant
  /** Stretch to the container width — the submit button in the design. */
  block?: boolean
}

const BASE =
  'inline-flex items-center justify-center gap-2 rounded-full font-medium transition outline-none focus-visible:ring-2 focus-visible:ring-brand-ring disabled:cursor-not-allowed aria-disabled:cursor-not-allowed'

const FILLED =
  'bg-brand text-white hover:bg-brand-hover disabled:bg-disabled disabled:text-disabled-ink aria-disabled:bg-disabled aria-disabled:text-disabled-ink aria-disabled:hover:bg-disabled'

/**
 * `aria-disabled` gets the same visual treatment as native `disabled`. The
 * checkout uses it so a blocked submit stays focusable and can explain itself
 * — a natively disabled button is unreachable by keyboard and silent to
 * screen readers.
 */
const VARIANTS: Record<Variant, string> = {
  // The form submit. Disabled styling is taken straight from the design, which
  // showed it greyed out while the fields were empty. Grows past one line
  // rather than spilling, for a long label on a phone.
  primary: `min-h-13 px-6 py-3 text-center text-base ${FILLED}`,
  // The same fill at the size of an inline action, such as opening Studio.
  compact: `h-10 px-4 text-sm ${FILLED}`,
  // Outlined, for the quiet action beside a value: edit, change, cancel.
  secondary:
    'h-10 border border-black/10 bg-white px-4 text-sm text-ink hover:border-brand hover:text-brand disabled:border-black/5 disabled:text-disabled-ink aria-disabled:border-black/5 aria-disabled:text-disabled-ink',
}

export function Button({
  variant = 'primary',
  block = false,
  type = 'button',
  ...props
}: ButtonProps) {
  return (
    <button
      {...props}
      type={type}
      className={`${BASE} ${VARIANTS[variant]} ${block ? 'w-full' : ''}`}
    />
  )
}
